import React, { useState, useEffect } from 'react';
import { AZURE } from '../../services/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Plus, FileText, ChevronLeft, Loader2, AlertTriangle } from 'lucide-react';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const STUDIO_LABELS = {
  pt: {
    subtitle: 'Estudos analíticos gerados a partir de dados reais do banco',
    newStudy: 'Novo Estudo',
    empty: 'Nenhum estudo gerado ainda. Clique em "Novo Estudo" para começar.',
    formTitle: 'Novo Estudo',
    distLabel: 'Distância (m)',
    strokeLabel: 'Nado',
    genderLabel: 'Gênero',
    compLabel: 'Competição',
    yearsLabel: 'Anos (separados por vírgula)',
    generating: 'Gerando...',
    generate: 'Gerar Estudo',
    cancel: 'Cancelar',
    evolution: 'Evolução',
    winnerDelta: 'Δ Tempo Vencedora:',
    medianDelta: 'Δ Mediana Final:',
    trend: 'Tendência:',
    competitiveness: 'Competitividade:',
    winnerChartTitle: 'Tempo da Vencedora por Ano',
    finalistsChartTitle: 'Finalistas por Ano',
    aiAnalysis: 'Análise Gerada',
    top5Title: 'Top 5 por Ano',
    genderOptions: { F: 'Feminino', M: 'Masculino' },
  },
  en: {
    subtitle: 'Analytical studies generated from real database data',
    newStudy: 'New Study',
    empty: 'No studies generated yet. Click "New Study" to start.',
    formTitle: 'New Study',
    distLabel: 'Distance (m)',
    strokeLabel: 'Stroke',
    genderLabel: 'Gender',
    compLabel: 'Competition',
    yearsLabel: 'Years (comma-separated)',
    generating: 'Generating...',
    generate: 'Generate Study',
    cancel: 'Cancel',
    evolution: 'Evolution',
    winnerDelta: 'Δ Winner Time:',
    medianDelta: 'Δ Final Median:',
    trend: 'Trend:',
    competitiveness: 'Competitiveness:',
    winnerChartTitle: "Winner's Time by Year",
    finalistsChartTitle: 'Finalists by Year',
    aiAnalysis: 'Generated Analysis',
    top5Title: 'Top 5 by Year',
    genderOptions: { F: 'Female', M: 'Male' },
  },
};

// ─── LIST VIEW ───────────────────────────────────────────────────────
function StudyListView({ studies, onSelect, onNew, t }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <h1 className="text-gray-900 text-2xl font-extrabold">Content Studio</h1>
            <p className="text-gray-500 text-sm">{t.subtitle}</p>
          </div>
        </div>
        <button
          onClick={onNew}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-bold"
          style={{ backgroundColor: BRAND_GREEN }}
        >
          <Plus size={18} /> {t.newStudy}
        </button>
      </div>

      {studies.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center text-gray-400">
          {t.empty}
        </div>
      ) : (
        <div className="grid gap-4">
          {studies.map((s, i) => (
            <div
              key={i}
              onClick={() => onSelect(s)}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition cursor-pointer flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
                <FileText size={20} style={{ color: BRAND_GREEN }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 truncate">{s.title}</h3>
                <p className="text-sm text-gray-500">
                  {s.event?.distance}m {s.event?.stroke_display} {s.event?.gender_display} • {s.years?.join(', ')}
                </p>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-medium">
                {s.status || 'draft'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── NEW STUDY FORM ──────────────────────────────────────────────────
function NewStudyForm({ onGenerate, onCancel, loading, t }) {
  const [distance, setDistance] = useState('100');
  const [stroke, setStroke] = useState('Borboleta');
  const [gender, setGender] = useState('F');
  const [competitionFamily, setCompetitionFamily] = useState('Maria Lenk');
  const [yearsStr, setYearsStr] = useState('2024, 2025, 2026');

  const handleSubmit = (e) => {
    e.preventDefault();
    const years = yearsStr.split(',').map(y => parseInt(y.trim())).filter(Boolean);
    onGenerate({ distance: parseInt(distance), stroke, gender, competition_family: competitionFamily, years });
  };

  return (
    <div className="bg-white rounded-xl border p-6 max-w-lg mx-auto">
      <h2 className="text-xl font-bold mb-4" style={{ color: BRAND_GREEN }}>{t.formTitle}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.distLabel}</label>
            <select value={distance} onChange={e => setDistance(e.target.value)} className="w-full border rounded-lg px-3 py-2">
              {[50, 100, 200, 400, 800, 1500].map(d => <option key={d} value={d}>{d}m</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.strokeLabel}</label>
            <select value={stroke} onChange={e => setStroke(e.target.value)} className="w-full border rounded-lg px-3 py-2">
              {['Borboleta', 'Livre', 'Costas', 'Peito', 'Medley'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.genderLabel}</label>
            <select value={gender} onChange={e => setGender(e.target.value)} className="w-full border rounded-lg px-3 py-2">
              <option value="F">{t.genderOptions.F}</option>
              <option value="M">{t.genderOptions.M}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.compLabel}</label>
            <input value={competitionFamily} onChange={e => setCompetitionFamily(e.target.value)} className="w-full border rounded-lg px-3 py-2" placeholder="Maria Lenk" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.yearsLabel}</label>
          <input value={yearsStr} onChange={e => setYearsStr(e.target.value)} className="w-full border rounded-lg px-3 py-2" placeholder="2024, 2025, 2026" />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={loading} className="flex-1 py-2 rounded-lg text-white font-bold flex items-center justify-center gap-2" style={{ backgroundColor: BRAND_GREEN }}>
            {loading ? <><Loader2 size={16} className="animate-spin" /> {t.generating}</> : t.generate}
          </button>
          <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-lg text-gray-600">{t.cancel}</button>
        </div>
      </form>
    </div>
  );
}

// ─── CANVAS VIEW (full study rendering) ──────────────────────────────
function StudyCanvasView({ study, onBack, t }) {
  if (!study) return null;

  const { title, subtitle, metrics_summary, evolution, charts, tables, ai_text, data_quality } = study;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft size={20} />
        </button>
        <div>
          <h1 className="text-gray-900 text-xl font-extrabold">{title}</h1>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>

      {/* Warnings */}
      {data_quality?.warnings?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle size={16} className="text-yellow-600 mt-0.5" />
          <div className="text-sm text-yellow-800">
            {data_quality.warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        </div>
      )}

      {/* Metrics Cards */}
      {metrics_summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {metrics_summary.map((m, i) => (
            <div key={i} className="bg-white rounded-xl border p-4">
              <div className="text-sm text-gray-500 mb-1">{m.year}</div>
              <div className="text-2xl font-extrabold" style={{ color: BRAND_GREEN }}>{m.winner_time_formatted || '—'}</div>
              <div className="text-sm text-gray-700 mt-1">{m.winner_name || '—'}</div>
              <div className="text-xs text-gray-400 mt-2">Final A: {m.finalists_count || 0} nadadoras • Total: {m.total_valid_results || 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Evolution */}
      {evolution && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-bold text-gray-900 mb-2">{t.evolution}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-gray-500">{t.winnerDelta}</span> <span className="font-bold">{evolution.winner_time_delta_formatted || '—'}</span></div>
            <div><span className="text-gray-500">{t.medianDelta}</span> <span className="font-bold">{evolution.median_final_delta_formatted || '—'}</span></div>
            <div><span className="text-gray-500">{t.trend}</span> <span className="font-bold">{evolution.trend || '—'}</span></div>
            <div><span className="text-gray-500">{t.competitiveness}</span> <span className="font-bold">{evolution.competitiveness_trend || '—'}</span></div>
          </div>
        </div>
      )}

      {/* Charts */}
      {charts?.winner_time_line && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-bold text-gray-900 mb-3">{t.winnerChartTitle}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={charts.winner_time_line}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis domain={['dataMin - 0.5', 'dataMax + 0.5']} />
              <Tooltip />
              <Line type="monotone" dataKey="time" stroke={BRAND_GREEN} strokeWidth={2} dot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {charts?.finalists_bar && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-bold text-gray-900 mb-3">{t.finalistsChartTitle}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={charts.finalists_bar}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill={BRAND_GOLD} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* AI Text */}
      {ai_text && (
        <div className="bg-white rounded-xl border p-5 space-y-4">
          <h3 className="font-bold text-gray-900">{t.aiAnalysis}</h3>
          {Object.entries(ai_text).map(([key, text]) => (
            <div key={key}>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tables */}
      {tables?.yearly_top5 && (
        <div className="bg-white rounded-xl border p-4 overflow-x-auto">
          <h3 className="font-bold text-gray-900 mb-3">{t.top5Title}</h3>
          {tables.yearly_top5.map((yearData, yi) => (
            <div key={yi} className="mb-4">
              <h4 className="text-sm font-semibold text-gray-600 mb-2">{yearData.year}</h4>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="pb-1">#</th><th className="pb-1">Atleta</th><th className="pb-1">Tempo</th><th className="pb-1">Fase</th>
                </tr></thead>
                <tbody>
                  {(yearData.rows || []).map((r, ri) => (
                    <tr key={ri} className="border-b border-gray-50">
                      <td className="py-1">{r.position}</td>
                      <td className="py-1">{r.athlete}</td>
                      <td className="py-1 font-mono">{r.time_formatted}</td>
                      <td className="py-1 text-gray-500">{r.phase}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE COMPONENT ─────────────────────────────────────────────
export default function GovernanceContentStudioPage() {
  const [view, setView] = useState('list'); // 'list' | 'new' | 'canvas'
  const [studies, setStudies] = useState([]);
  const [activeStudy, setActiveStudy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { lang } = useGovernanceLang();
  const t = STUDIO_LABELS[lang] || STUDIO_LABELS.pt;

  // Load saved studies list
  useEffect(() => {
    AZURE.get('/governance/content-studio/studies')
      .then(res => setStudies(res.data?.data || []))
      .catch(() => {}); // ignore if endpoint not ready
  }, []);

  // Load pilot as first study if list is empty
  useEffect(() => {
    if (studies.length === 0) {
      AZURE.get('/governance/content-studio/pilot/100-fly-female-maria-lenk')
        .then(res => {
          if (res.data?.success) {
            setStudies([res.data.data]);
          }
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const handleGenerate = async (params) => {
    setLoading(true);
    setError(null);
    try {
      const res = await AZURE.post('/governance/content-studio/generate', params);
      if (res.data?.success) {
        const newStudy = res.data.data;
        setStudies(prev => [newStudy, ...prev]);
        setActiveStudy(newStudy);
        setView('canvas');
      } else {
        setError(res.data?.error || 'Erro ao gerar estudo');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
        )}

        {view === 'list' && (
          <StudyListView
            studies={studies}
            onSelect={(s) => { setActiveStudy(s); setView('canvas'); }}
            onNew={() => setView('new')}
            t={t}
          />
        )}

        {view === 'new' && (
          <NewStudyForm
            onGenerate={handleGenerate}
            onCancel={() => setView('list')}
            loading={loading}
            t={t}
          />
        )}

        {view === 'canvas' && (
          <StudyCanvasView
            study={activeStudy}
            onBack={() => setView('list')}
            t={t}
          />
        )}
      </div>
    </div>
  );
}
